import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  ApiResponse,
  LoanAccountItem,
  LoanEmployeeItem,
  LoanLookupItem,
  LoanRequestListItem,
  LoanRequestListResponse,
  LoanRequestVm,
} from '@/api/types'

class LoanRequestService {
  async getAll(
    pageNumber: number,
    pageSize: number,
    branchId: number,
    codeType: string
  ): Promise<LoanRequestListResponse> {
    const url = endpoints.loanRequest.getAll(pageNumber, pageSize, branchId, codeType)
    const response = await apiClient.get<ApiResponse<LoanRequestListResponse> | any>(url)
    const payload = response.data

    const raw = (payload && typeof payload === 'object' && 'data' in payload) ? payload.data : payload
    if (raw && typeof raw === 'object') {
      const arr = Array.isArray(raw.data) ? raw.data : Array.isArray(raw) ? raw : []
      return {
        totalRecords: raw.totalRecords ?? payload.totalRecords ?? arr.length ?? 0,
        pageNumber: raw.pageNumber ?? payload.pageNumber ?? pageNumber,
        pageSize: raw.pageSize ?? payload.pageSize ?? pageSize,
        data: (arr as any[]).map(normalizeLoanRequestListItem),
      }
    }

    return { totalRecords: 0, pageNumber, pageSize, data: [] }
  }

  async getById(id: number, branchId: number): Promise<LoanRequestVm | null> {
    const url = endpoints.loanRequest.getById(id, branchId)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    const payload = response.data
    const raw = (payload && typeof payload === 'object' && 'data' in payload) ? payload.data : payload
    if (!raw || typeof raw !== 'object') return null

    return {
      id: raw.id ?? raw.Id ?? 0,
      empId: raw.empId ?? raw.EmpId ?? 0,
      startDate: raw.startDate ?? raw.StartDate ?? '',
      loanAmount: raw.loanAmount ?? raw.LoanAmount ?? 0,
      noOfInstallment: raw.noOfInstallment ?? raw.NoOfInstallment ?? 0,
      deductAmount: raw.deductAmount ?? raw.DeductAmount ?? 0,
      loanTypeId: raw.loanTypeId ?? raw.LoanTypeId ?? 0,
      details: raw.details ?? raw.Details ?? '',
      status: raw.status ?? raw.Status ?? 0,
      branchId: raw.branchId ?? raw.BranchId ?? branchId,
      departmentId: raw.departmentId ?? raw.DepartmentId ?? 0,
      creditAccount: raw.creditAccount ?? raw.CreditAccount ?? 0,
      attachmentUrl: raw.attachmentUrl ?? raw.AttachmentUrl,
    }
  }

  async getLoanTypes(): Promise<LoanLookupItem[]> {
    const url = endpoints.loanRequest.getLoanTypes
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] =
      Array.isArray(payload) ? payload :
      Array.isArray(payload?.data) ? payload.data :
      []
    return list.map(normalizeLookupItem)
  }

  async getStatusLookups(): Promise<LoanLookupItem[]> {
    const url = endpoints.loanRequest.getStatusLookups
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] =
      Array.isArray(payload) ? payload :
      Array.isArray(payload?.data) ? payload.data :
      []
    return list.map(normalizeLookupItem)
  }

  async getDepartments(_branchId?: number): Promise<LoanLookupItem[]> {
    // Use Employee/GetDepartment (no parameters) per API docs
    const url = endpoints.employeeLookups.departments
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] =
      Array.isArray(payload) ? payload :
      Array.isArray(payload?.data) ? payload.data :
      []
    return list.map(normalizeLookupItem)
  }

  async getActiveEmployees(branchId: number): Promise<LoanEmployeeItem[]> {
    const url = endpoints.loanRequest.getActiveEmployees(branchId)
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] =
      Array.isArray(payload) ? payload :
      Array.isArray(payload?.data) ? payload.data :
      []
    return list.map((e) => ({
      id: e.id ?? e.Id ?? e.empId ?? e.EmpId ?? 0,
      empId: e.empId ?? e.EmpId ?? e.id ?? e.Id,
      fullName:
        e.fullName ??
        e.FullName ??
        [
          e.empFirstNameEn ?? e.EmpFirstNameEn,
          e.empFatherNameEn ?? e.EmpFatherNameEn,
          e.empSecondNameEn ?? e.EmpSecondNameEn,
          e.empLastNameEn ?? e.EmpLastNameEn,
        ]
          .filter(Boolean)
          .join(' ')
          .trim(),
      departmentId: e.departmentId ?? e.DepartmentId ?? e.empDepartmetId ?? e.EmpDepartmetId,
      departmentName: e.departmentName ?? e.DepartmentName,
    }))
  }

  async getAccounts(branchId: number): Promise<LoanAccountItem[]> {
    const url = endpoints.loanRequest.getAccounts(branchId)
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] =
      Array.isArray(payload) ? payload :
      Array.isArray(payload?.data) ? payload.data :
      []
    return list.map((a) => ({
      id: a.id ?? a.Id ?? a.accountId ?? a.AccountId ?? 0,
      accountName:
        a.accountName ??
        a.AccountName ??
        a.accountNameEn ??
        a.AccountNameEn ??
        a.accountNameAr ??
        a.AccountNameAr ??
        a.nameEn ??
        a.NameEn ??
        a.nameAr ??
        a.NameAr,
      nameEn: a.nameEn ?? a.NameEn ?? a.accountNameEn ?? a.AccountNameEn,
      nameAr: a.nameAr ?? a.NameAr ?? a.accountNameAr ?? a.AccountNameAr,
      accountCode: a.accountCode ?? a.AccountCode,
    }))
  }

  async create(vm: LoanRequestVm, file?: File | null): Promise<any> {
    const formData = toFormData(vm, file)
    const formDataDebug: Record<string, string> = {}
    for (const [key, value] of formData.entries()) {
      if (typeof value === 'string') {
        formDataDebug[key] = value
      } else {
        const v = value as any
        const name = v?.name ? String(v.name) : 'Blob'
        formDataDebug[key] = `[${v?.constructor?.name ?? 'Blob'}:${name}]`
      }
    }
    console.log('Loan Request Save Payload:', { vm, formData: formDataDebug })
    const response = await apiClient.post(endpoints.loanRequest.create, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async update(vm: LoanRequestVm, file?: File | null): Promise<any> {
    const formData = toFormData(vm, file)
    const formDataDebug: Record<string, string> = {}
    for (const [key, value] of formData.entries()) {
      if (typeof value === 'string') {
        formDataDebug[key] = value
      } else {
        const v = value as any
        const name = v?.name ? String(v.name) : 'Blob'
        formDataDebug[key] = `[${v?.constructor?.name ?? 'Blob'}:${name}]`
      }
    }
    console.log('Loan Request Save Payload:', { vm, formData: formDataDebug })
    const response = await apiClient.put(endpoints.loanRequest.update, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async delete(id: number): Promise<any> {
    const url = endpoints.loanRequest.delete(id)
    const response = await apiClient.delete(url)
    return response.data
  }
}

function normalizeLookupItem(item: any): LoanLookupItem {
  const id = item.id ?? item.Id ?? 0
  const lookupCodeId = item.lookupCodeId ?? item.LookupCodeId ?? item.lookupCodeID
  return {
    id,
    lookupCodeId: lookupCodeId != null ? Number(lookupCodeId) : undefined,
    lookupNameEn: item.lookupNameEn ?? item.LookupNameEn,
    lookupNameAr: item.lookupNameAr ?? item.LookupNameAr,
    nameEn: item.nameEn ?? item.NameEn,
    nameAr: item.nameAr ?? item.NameAr,
    name: item.name ?? item.Name,
    codeType: item.codeType ?? item.CodeType,
  }
}

function normalizeLoanRequestListItem(item: any): LoanRequestListItem {
  return {
    id: item.id ?? item.Id ?? 0,
    loanNo: item.loanNo ?? item.LoanNo,
    empId: item.empId ?? item.EmpId,
    employeeName: item.employeeName ?? item.EmployeeName ?? item.empName ?? item.EmpName,
    departmentId: item.departmentId ?? item.DepartmentId,
    departmentName: item.departmentName ?? item.DepartmentName,
    startDate: item.startDate ?? item.StartDate ?? null,
    loanAmount: item.loanAmount ?? item.LoanAmount,
    noOfInstallment: item.noOfInstallment ?? item.NoOfInstallment,
    deductAmount: item.deductAmount ?? item.DeductAmount,
    status: item.status ?? item.Status,
    statusName: item.statusName ?? item.StatusName,
    approvalDate: item.approvalDate ?? item.ApprovalDate ?? null,
    createdDate: item.createdDate ?? item.CreatedDate ?? null,
    isEditable: item.isEditable ?? item.IsEditable,
    isPaid: item.isPaid ?? item.IsPaid,
    creditAccount: item.creditAccount ?? item.CreditAccount,
  }
}

function toFormData(vm: LoanRequestVm, file?: File | null): FormData {
  const fd = new FormData()
  fd.append('Id', String(vm.id ?? 0))
  fd.append('EmpId', String(vm.empId ?? 0))
  fd.append('StartDate', vm.startDate ?? '')
  fd.append('LoanAmount', String(vm.loanAmount ?? 0))
  fd.append('NoOfInstallment', String(vm.noOfInstallment ?? 0))
  fd.append('DeductAmount', String(vm.deductAmount ?? 0))
  fd.append('LoanTypeId', String(vm.loanTypeId ?? 0))
  fd.append('Details', vm.details ?? '')
  fd.append('Status', String(vm.status ?? 0))
  fd.append('BranchId', String(vm.branchId ?? 0))
  fd.append('DepartmentId', String(vm.departmentId ?? 0))
  fd.append('CreditAccount', String(vm.creditAccount ?? 0))
  if (vm.attachmentUrl) fd.append('AttachmentUrl', vm.attachmentUrl)
  if (file) fd.append('IMG', file)
  return fd
}

export const loanRequestService = new LoanRequestService()

